import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import {
  ConfirmDialog,
  Drawer,
  ErrorBanner,
  Spinner,
} from '@saas-kit/ui';

import { getInvoice } from './api';
import { InvoiceActionsFooter } from './InvoiceActionsFooter';
import { InvoiceDetailBody } from './InvoiceDetailBody';
import { PaymentsList } from './PaymentsList';
import { RecordPaymentDialog } from './RecordPaymentDialog';
import type { AdminInvoice } from './types';
import { useInvoiceMutations } from './useInvoiceMutations';

interface Props {
  tenantId: string;
  invoice: AdminInvoice;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type ConfirmAction = 'issue' | 'mark_paid' | 'void' | null;

export function InvoiceDetailDrawer({
  tenantId,
  invoice,
  open,
  onOpenChange,
}: Props): JSX.Element {
  const { t } = useTranslation('adminBilling');
  const [confirming, setConfirming] = useState<ConfirmAction>(null);
  const [paymentOpen, setPaymentOpen] = useState(false);

  const detailQuery = useQuery({
    queryKey: ['admin-billing-invoice', tenantId, invoice.id],
    queryFn: () => getInvoice(tenantId, invoice.id),
    enabled: open,
  });
  const current = detailQuery.data?.invoice ?? invoice;
  const payments = detailQuery.data?.payments ?? [];

  const m = useInvoiceMutations({ tenantId, invoiceId: current.id });

  const outstanding =
    Number(current.total_amount) - Number(current.amount_paid);
  const outstandingDisplay = outstanding > 0 ? outstanding.toFixed(2) : '0.00';

  const closeConfirm = { onSuccess: () => setConfirming(null) };

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={t('detail.title', { number: current.invoice_number })}
      description={t('detail.description')}
      testId="invoice-detail-drawer"
      width="lg"
      footer={
        <InvoiceActionsFooter
          invoice={current}
          canIssue={current.status === 'draft'}
          canMarkPaid={
            current.status === 'issued' || current.status === 'past_due'
          }
          canVoid={current.status !== 'paid' && current.status !== 'void'}
          canRecordPayment={
            (current.status === 'issued' || current.status === 'past_due') &&
            outstanding > 0
          }
          issuePending={m.issue.isPending}
          markPaidPending={m.markPaid.isPending}
          voidPending={m.void.isPending}
          onIssue={() => setConfirming('issue')}
          onMarkPaid={() => setConfirming('mark_paid')}
          onVoid={() => setConfirming('void')}
          onRecordPayment={() => setPaymentOpen(true)}
        />
      }
    >
      {detailQuery.isError ? (
        <ErrorBanner>{t('errors.load_failed')}</ErrorBanner>
      ) : null}
      {detailQuery.isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : (
        <div className="space-y-5">
          <InvoiceDetailBody invoice={current} outstanding={outstandingDisplay} />
          <PaymentsList
            payments={payments}
            currency={current.currency}
            isVoiding={m.voidPayment.isPending}
            voidingId={m.voidPayment.variables ?? null}
            onVoid={(id) => m.voidPayment.mutate(id)}
          />
        </div>
      )}

      {confirming === 'issue' ? (
        <ConfirmDialog
          title={t('confirm.issue_title')}
          body={t('confirm.issue_body', { number: current.invoice_number })}
          confirmLabel={t('detail.actions.issue')}
          isBusy={m.issue.isPending}
          onCancel={() => setConfirming(null)}
          onConfirm={() => m.issue.mutate({}, closeConfirm)}
        />
      ) : null}
      {confirming === 'mark_paid' ? (
        <ConfirmDialog
          title={t('confirm.mark_paid_title')}
          body={t('confirm.mark_paid_body', { number: current.invoice_number })}
          confirmLabel={t('detail.actions.mark_paid')}
          isBusy={m.markPaid.isPending}
          onCancel={() => setConfirming(null)}
          onConfirm={() => m.markPaid.mutate({}, closeConfirm)}
        />
      ) : null}
      {confirming === 'void' ? (
        <ConfirmDialog
          title={t('confirm.void_title')}
          body={t('confirm.void_body', { number: current.invoice_number })}
          confirmLabel={t('detail.actions.void')}
          variant="destructive"
          isBusy={m.void.isPending}
          onCancel={() => setConfirming(null)}
          onConfirm={() => m.void.mutate({}, closeConfirm)}
        />
      ) : null}

      <RecordPaymentDialog
        open={paymentOpen}
        invoiceNumber={current.invoice_number}
        currency={current.currency}
        outstandingAmount={outstandingDisplay}
        isPending={m.recordPayment.isPending}
        onOpenChange={setPaymentOpen}
        onSubmit={(payload) =>
          m.recordPayment.mutateAsync(payload).then(() => undefined)
        }
      />
    </Drawer>
  );
}
